import { useMemo, useState } from 'react';
import {
  buildPermissionTree,
  clearAllLeafIds,
  findLeafNodeIds,
  getNodeCheckState,
  selectAllLeafIds,
  toggleNodeCheck,
} from './permissionTreeUtils';

function TreeNode({ node, checkedLeafIds, onToggle, defaultExpanded }) {
  const hasChildren = node.children?.length > 0;
  const [expanded, setExpanded] = useState(defaultExpanded);
  const checkState = getNodeCheckState(node, checkedLeafIds);

  function handleCheckChange(event) {
    event.stopPropagation();
    onToggle(node, event.target.checked);
  }

  return (
    <li className="permission-tree__item">
      <div className="permission-tree__row">
        {hasChildren ? (
          <button
            type="button"
            className="permission-tree__expand"
            aria-expanded={expanded}
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? '▼' : '▶'}
          </button>
        ) : (
          <span className="permission-tree__expand permission-tree__expand--placeholder" aria-hidden="true" />
        )}
        <label className="permission-tree__label">
          <input
            type="checkbox"
            checked={checkState === 'checked'}
            ref={(el) => {
              if (el) {
                el.indeterminate = checkState === 'indeterminate';
              }
            }}
            onChange={handleCheckChange}
          />
          <span>{node.title || node.name || `權限 ${node.id}`}</span>
        </label>
      </div>
      {hasChildren && expanded && (
        <ul className="permission-tree__children">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              checkedLeafIds={checkedLeafIds}
              onToggle={onToggle}
              defaultExpanded={false}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function PermissionTree({ permissions, checkedLeafIds, onChange, disabled = false }) {
  const tree = useMemo(() => buildPermissionTree(permissions), [permissions]);
  const allLeafIds = useMemo(() => findLeafNodeIds(permissions), [permissions]);

  function handleToggle(node, nextChecked) {
    if (disabled) {
      return;
    }
    onChange(toggleNodeCheck(node, checkedLeafIds, nextChecked));
  }

  function handleSelectAll() {
    if (disabled) {
      return;
    }
    onChange(selectAllLeafIds(allLeafIds));
  }

  function handleClearAll() {
    if (disabled) {
      return;
    }
    onChange(clearAllLeafIds());
  }

  if (!permissions?.length) {
    return <p className="permission-tree__empty">尚無權限資料</p>;
  }

  return (
    <div className="permission-tree">
      <div className="permission-tree__toolbar">
        <span className="permission-tree__title">菜單權限</span>
        <div className="permission-tree__bulk">
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleSelectAll} disabled={disabled}>
            全選
          </button>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleClearAll} disabled={disabled}>
            全不選
          </button>
        </div>
      </div>
      <ul className="permission-tree__root">
        {tree.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            checkedLeafIds={checkedLeafIds}
            onToggle={handleToggle}
            defaultExpanded
          />
        ))}
      </ul>
    </div>
  );
}
